import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  Queue as QueueIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';

const ProcessingQueue: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<any>(null);
  const [formData, setFormData] = useState({
    materialType: '',
    quantity: '',
    priority: 'Normal',
    source: '',
    destination: '',
    scheduledTime: '',
    status: 'Pending',
    notes: '',
  });

  // Mock data for demonstration
  const queueItems = [
    {
      id: '1',
      materialType: 'Mixed Plastic',
      quantity: '5 tons',
      priority: 'High',
      source: 'Collection Point A',
      destination: 'Central Processing Facility',
      scheduledTime: '2024-03-20 14:00',
      status: 'In Progress',
      progress: 65,
      estimatedCompletion: '2024-03-20 16:00',
      assignedTo: 'Processing Line 1',
    },
    // Add more mock queue items as needed
  ];

  const materialTypes = [
    'Mixed Plastic',
    'Paper',
    'Metal',
    'Glass',
    'Organic',
    'Hazardous',
  ];

  const priorities = [
    'Low',
    'Normal',
    'High',
    'Urgent',
  ];

  const processingLines = [
    'Processing Line 1',
    'Processing Line 2',
    'Processing Line 3',
  ];

  const handleOpen = (queue?: any) => {
    if (queue) {
      setSelectedQueue(queue);
      setFormData({
        materialType: queue.materialType,
        quantity: queue.quantity,
        priority: queue.priority,
        source: queue.source,
        destination: queue.destination,
        scheduledTime: queue.scheduledTime,
        status: queue.status,
        notes: '',
      });
    } else {
      setSelectedQueue(null);
      setFormData({
        materialType: '',
        quantity: '',
        priority: 'Normal',
        source: '',
        destination: '',
        scheduledTime: '',
        status: 'Pending',
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQueue(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        icon={<QueueIcon />}
        title={t('processing.queue.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('processing.queue.addToQueue')}
          </Button>
        }
      />

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.queue.material')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.quantity')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.priority')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.source')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.destination')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.queue.scheduled')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.queue.progress')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queueItems.map((item) => (
                <TableRow 
                  key={item.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.materialType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.assignedTo}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    <StatusChip kind="priority" label={item.priority} value={item.priority} />
                  </TableCell>
                  <TableCell>{item.source}</TableCell>
                  <TableCell>{item.destination}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                      <Typography variant="body2">
                        {item.scheduledTime}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('processing.queue.estCompletion', { value: item.estimatedCompletion })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={item.progress} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.progress}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip label={item.status} value={item.status} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      sx={{ 
                        color: theme.palette.success.main,
                        '&:hover': { 
                          bgcolor: theme.palette.success.light,
                          color: theme.palette.success.contrastText,
                        },
                      }}
                    >
                      <PlayIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.warning.main,
                        '&:hover': { 
                          bgcolor: theme.palette.warning.light,
                          color: theme.palette.warning.contrastText,
                        },
                      }}
                    >
                      <PauseIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <StopIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpen(item)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {selectedQueue ? t('processing.queue.editDialogTitle') : t('processing.queue.addDialogTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.materialType')}</InputLabel>
                  <Select
                    value={formData.materialType}
                    onChange={(e) => setFormData({ ...formData, materialType: e.target.value })}
                    label={t('common.materialType')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {materialTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.quantity')}
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.queue.quantityPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.priority')}</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    label={t('common.priority')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {priorities.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.source')}
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={processingLines}
                  value={formData.destination}
                  onChange={(_, newValue) => setFormData({ ...formData, destination: newValue || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.destination')}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.scheduledTime')}
                  type="datetime-local"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('common.status')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    <MenuItem value="Pending">{t('processing.queue.statusPending')}</MenuItem>
                    <MenuItem value="In Progress">{t('processing.queue.statusInProgress')}</MenuItem>
                    <MenuItem value="Completed">{t('processing.queue.statusCompleted')}</MenuItem>
                    <MenuItem value="Cancelled">{t('processing.queue.statusCancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedQueue ? t('common.update') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProcessingQueue; 